// Vendors
import { useEffect, useImperativeHandle, forwardRef, useRef, useState } from 'react';
// Models
import { Notes } from '@/api/models/Notes';
import { NotesByContractRequest } from '@/api/models/BaseRequest';
import { ExportNotesAction, NotesTabProps } from '@/utils/types/notesTab';

// Store
import { fetchNotesStart } from '@/store/features/notes/notesSlice';
import Loader from '@/components/atoms/Loader/Loader';
import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/hooks/storeHook';

// Asserts 
import ArrowUp from '@/assets/icons/arrow-up.svg';

const NotesTab = forwardRef<ExportNotesAction, NotesTabProps>(
  ({ contractId,clientId, onNotesAvailabilityChange }, ref) => {
    const dispatch = useAppDispatch();
    const [showScrollTop, setShowScrollTop] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { notes, loading } = useAppSelector(
      (state: {
        notes: {
          notes: Notes[];
          loading: boolean;
        };
      }) => state.notes,
    );
 
    const sortedNotes = [...notes].sort((a, b) => {
      const parseDateTime = (date: string) => new Date(date).getTime();
      return parseDateTime(b.addedOn) - parseDateTime(a.addedOn);
    });
 
  
    // Scroll to top functionality
    const scrollToTop = () => {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };
 
    useImperativeHandle(ref, () => ({
      handleExportNotes() {
        if (!sortedNotes.length) return;
 
        const csvHeader = ['Date', 'Time', 'Subject', 'User', 'Note Content'];
        const csvRows = sortedNotes.map((note) => [
          formatDate(note.addedOn) || '-',
          formateTime(note.addedOn) || '-',
          note.subject || '-',
          note.addedBy || '-',
          note.noteText.replace(/\n/g, ' ') || '-',
        ]);
 
        const csvContent = [csvHeader, ...csvRows]
          .map((row) => row.map((field) => `"${field}"`).join(','))
          .join('\n');
 
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'notes_export.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    }));
 
    useEffect(() => {
      if (onNotesAvailabilityChange) {
        onNotesAvailabilityChange(sortedNotes.length > 0);
      }
    }, [sortedNotes, onNotesAvailabilityChange]);
 
    useEffect(() => {
      if (contractId) {
        const request: NotesByContractRequest = {
          contractId: contractId,
          clientId: clientId,
          keyword: '',
          pageNo: 0,
          pageSize: 0,
        };
        dispatch(fetchNotesStart(request));
      }
    }, [contractId, dispatch]);
 
    const formatDate = (DateStr: string) => {
      const date = new Date(DateStr);
      return (
        `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
        `${date.getDate().toString().padStart(2, '0')}/` +
        date.getFullYear().toString().slice(-2)
      );
    };
 
    const formateTime = (dateStr: string) => {
      const time = new Date(dateStr).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return time;
    };
 
    if (loading) return <Loader />;
 
    return (
<>
        {sortedNotes.length == 0 && (
<div className="no-data-message">
<Typography className="no-data-text">No notes have been added yet</Typography>
</div>
        )}
         <div className="notes-list_scrollable-wrapper">
<div className="notes-tab-main"  ref={containerRef}
onScroll={(e) => {
  const el = e.currentTarget;
  setShowScrollTop( el.scrollTop > 10);
}}>
          {sortedNotes.map((note, index) => {
            return (
<div key={index} className="notes-timeline_wrap">
<div className="date-info_wrap">
<div className="date-info-conatiner">
<Typography className="date">{formatDate(note.addedOn)}</Typography>
<Typography className="date">{formateTime(note.addedOn)}</Typography>
</div>
</div>
 
                <div className="notes-timeline_container">
<div className="notes-timeline">
<div className="list_wrap">
<Typography className="heading">
                        {note.subject} | {note.addedBy}
</Typography>
<Typography className="note_text">{note.noteText}</Typography>
</div>
</div>
</div>
</div>
            );
          })}
         
</div>
 {showScrollTop && (
<div className="scroll-to-top" onClick={scrollToTop}>
<img src={ArrowUp} alt="info-icon" className="scroll-icon" />
</div>
          )}
</div>
</>
    );
  },
);
 
NotesTab.displayName = 'NotesTab';
export default NotesTab;
